import React, { useMemo } from 'react';

// ─── Seeded pseudo-random ────────────────────────────────────────────────────

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function randRange(rng: () => number, min: number, max: number): number {
  return min + rng() * (max - min);
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

// ─── Trend data generation ───────────────────────────────────────────────────

const POINT_COUNT = 24;
const SEED = 42;
const VIEWBOX_W = 100;
const VIEWBOX_H = 40;

/** Y positions for 3 faint horizontal grid lines (at 25%, 50%, 75% of viewBox height). */
const GRID_LINE_YS = [VIEWBOX_H * 0.25, VIEWBOX_H * 0.5, VIEWBOX_H * 0.75];

interface Point {
  x: number;
  y: number;
}

/**
 * Generate realistic upward-trending analytics data — 24 points.
 * Starts low on chart (high y-value), trends upward (decreasing y).
 * Small random noise for natural fluctuations.
 * Y values clamped to [0.15, 0.85] of viewBox height to leave room for grid lines.
 */
function generateTrendPoints(): Point[] {
  const rng = seededRandom(SEED);
  const points: Point[] = [];
  const slope = randRange(rng, 0.018, 0.028);
  const startY = randRange(rng, 0.65, 0.8);

  for (let i = 0; i < POINT_COUNT; i++) {
    const x = (i / (POINT_COUNT - 1)) * VIEWBOX_W;
    const y = clamp(startY - i * slope + randRange(rng, -0.04, 0.04), 0.15, 0.85);
    points.push({ x, y: y * VIEWBOX_H });
  }

  return points;
}

// ─── Smooth path via Catmull-Rom → cubic bezier ──────────────────────────────

function smoothPath(points: Point[]): string {
  if (points.length < 2) return '';

  const parts: string[] = [`M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`];

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const tension = 6;
    const cp1x = p1.x + (p2.x - p0.x) / tension;
    const cp1y = p1.y + (p2.y - p0.y) / tension;
    const cp2x = p2.x - (p3.x - p1.x) / tension;
    const cp2y = p2.y - (p3.y - p1.y) / tension;

    parts.push(
      `C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`,
    );
  }

  return parts.join(' ');
}

/**
 * Build closed area path — same curve, then down to bottom-right,
 * across to bottom-left, close.
 */
function areaPath(linePath: string, points: Point[]): string {
  if (points.length < 2) return '';
  const last = points[points.length - 1];
  const first = points[0];
  return `${linePath} L${last.x.toFixed(1)},${VIEWBOX_H} L${first.x.toFixed(1)},${VIEWBOX_H} Z`;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Line/Area chart skeleton with:
 * - 3 faint horizontal grid lines simulating Y-axis gridlines
 * - Left padding for Y-axis space
 * - Bottom padding for X-axis space
 * - Line draws left-to-right via stroke-dashoffset
 * - Area fill appears top-to-bottom via animated clipPath rect
 */
interface LineChartSkeletonProps {
  /** Real chart title from plan preview — replaces shimmer placeholder. */
  title?: string;
}

const LineChartSkeleton: React.FC<LineChartSkeletonProps> = ({ title }) => {
  const points = useMemo(() => generateTrendPoints(), []);
  const lineD = useMemo(() => smoothPath(points), [points]);
  const areaD = useMemo(() => areaPath(lineD, points), [lineD, points]);

  return (
    <div className="skeleton-chart-container">
      <div className="line-skeleton">
        {title ? (
          <div className="chart-skeleton__real-title">{title}</div>
        ) : (
          <div className="skeleton-block line-skeleton__title" />
        )}

        <div className="line-skeleton__chart">
          <svg
            className="line-skeleton__svg"
            viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
            preserveAspectRatio="none"
            fill="none"
          >
            <defs>
              {/* Animated clip rect — reveals area from top to bottom */}
              <clipPath id="area-reveal-clip">
                <rect
                  className="line-skeleton__clip-rect"
                  x="0"
                  y="0"
                  width={VIEWBOX_W}
                  height={VIEWBOX_H}
                />
              </clipPath>
            </defs>

            {/* 3 faint horizontal grid lines */}
            {GRID_LINE_YS.map((y, i) => (
              <line
                key={i}
                className="line-skeleton__grid-line"
                x1="0"
                y1={y}
                x2={VIEWBOX_W}
                y2={y}
              />
            ))}

            {/* Area fill — clipped for top-to-bottom reveal */}
            <path
              className="line-skeleton__area"
              d={areaD}
              clipPath="url(#area-reveal-clip)"
            />

            {/* Line stroke — draws left-to-right */}
            <path className="line-skeleton__path" d={lineD} />
          </svg>
        </div>

        <div className="line-skeleton__axis" />
      </div>
    </div>
  );
};

export default React.memo(LineChartSkeleton);